import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Form, Icon, Input, Modal } from "semantic-ui-react";

import "components/DBB/components/DBBCreateContractModal/components/DBBTyreSearch/DBBTyreSearch.scss";
import { useDBBTyreSearch } from "components/DBB/components/DBBCreateContractModal/components/DBBTyreSearch/hooks";
import { Dropdown } from "components/Dropdown";
import { ReactTable } from "components/ReactTable";
import { getCarSeasonOptions, getTyreSeasonIcon, parseIntOrDefault } from "utils/common";

import type { Car, Tyre } from "models";
import type { FormEvent } from "react";

export enum DBB_TYRE_STATES {
  OnCar = "C",
  OnStorage = "S"
}

type DBBTyreSearchProps = {
  car: Car;
  open: boolean;
  onClose: () => void;
  onConfirm: (tyre: Tyre) => void;
};

const columnHelper = createColumnHelper<Tyre>();

export const DBBTyreSearch = ({ car, open, onClose, onConfirm }: DBBTyreSearchProps) => {
  const { t } = useTranslation();

  const [pickedTyre, setPickedTyre] = useState<Tyre | null>(null);

  const tyreSearch = useDBBTyreSearch(car, open);

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setPickedTyre(null);
    tyreSearch.searchTyres();
  };

  const handleClose = () => {
    setPickedTyre(null);
    onClose();
  };

  const handleConfirm = () => {
    if (!pickedTyre) return;

    onConfirm(pickedTyre);
    handleClose();
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row.season, {
        id: "season",
        header: "",
        size: 30,
        cell: cell => <Icon className={getTyreSeasonIcon(cell.getValue())} />
      }),
      columnHelper.accessor(row => row.description, {
        id: "description",
        header: () => t("v8_description", "Description"),
        size: 330,
        cell: cell => cell.getValue()
      }),
      columnHelper.accessor(row => row.width, {
        id: "width",
        header: () => t("v8_width", "Width"),
        size: 50,
        cell: cell => cell.getValue()
      }),
      columnHelper.accessor(row => row.height, {
        id: "height",
        header: () => t("v8_height", "Height"),
        size: 50,
        cell: cell => cell.getValue()
      }),
      columnHelper.accessor(row => row.size, {
        id: "size",
        header: () => t("v8_size", "Size"),
        size: 50,
        cell: cell => cell.getValue()
      }),
      columnHelper.accessor(row => row.speed, {
        id: "speed",
        header: () => t("v8_speed", "Speed"),
        size: 50,
        cell: cell => cell.getValue()
      }),
      columnHelper.accessor(row => row.manufacturer, {
        id: "brand",
        header: () => t("v8_brand", "Brand"),
        size: 120,
        cell: cell => cell.getValue()
      }),
      columnHelper.accessor(row => row, {
        id: "pick",
        header: () => null,
        size: 75,
        cell: ({ row: { original } }) => <Checkbox checked={original.id === pickedTyre?.id} onChange={() => setPickedTyre(original)} />
      })
    ],
    [t, pickedTyre]
  );

  return (
    <Modal className="DBBTyreSearch" size="large" open={open} onClose={handleClose} closeOnDimmerClick={false}>
      <Modal.Header>{t("v8_add_tyre", "Add tyre")}</Modal.Header>
      <Modal.Content scrolling>
        <Form onSubmit={handleSearchSubmit}>
          <Form.Group>
            <Form.Field width={2} required>
              <label>{t("v8_width", "Width")}</label>
              <Input
                value={tyreSearch.width || ""}
                placeholder={t("v8_width", "Width")}
                fluid
                name="width"
                required
                onChange={e => tyreSearch.setWidth(parseIntOrDefault(e.target.value, null))}
              />
            </Form.Field>
            <Form.Field width={2} disabled={!tyreSearch.width}>
              <label>{t("v8_height", "Height")}</label>
              <Input
                value={tyreSearch.height || ""}
                placeholder={t("v8_height", "Height")}
                fluid
                name="height"
                onChange={e => tyreSearch.setHeight(parseIntOrDefault(e.target.value, null))}
              />
            </Form.Field>
            <Form.Field width={2} disabled={!tyreSearch.width || !tyreSearch.height}>
              <label>{t("v8_size", "Size")}</label>
              <Input
                value={tyreSearch.size || ""}
                placeholder={t("v8_size", "Size")}
                fluid
                name="size"
                onChange={e => tyreSearch.setSize(parseIntOrDefault(e.target.value, null))}
              />
            </Form.Field>
            <Form.Field width={6}>
              <label>{t("v8_brand", "Brand")}</label>
              <Dropdown
                selection
                selectOnBlur={false}
                loading={tyreSearch.isFetchingBrands}
                options={tyreSearch.tyreBrandOptions}
                clearable
                search
                value={tyreSearch.manufacturer}
                onChange={value => tyreSearch.setManufacturer(value ?? "")}
                name="manufacturer"
              />
            </Form.Field>
            <Form.Field width={4}>
              <label>{t("v8_season", "Season")}</label>
              <Dropdown
                selection
                selectOnBlur={false}
                clearable
                options={getCarSeasonOptions(t)}
                value={tyreSearch.season ?? undefined}
                onChange={season => tyreSearch.setSeason(season ?? null)}
                name="season"
              />
            </Form.Field>
            <Form.Field>
              <Button type="submit" className="search-button" disabled={!tyreSearch.width || tyreSearch.isSearching} loading={tyreSearch.isSearching}>
                {t("v8_search", "Search")}
              </Button>
            </Form.Field>
          </Form.Group>
        </Form>

        {!tyreSearch.isSearching && !tyreSearch.tyres.length && (
          <div className="no-results">
            <p>{t("v8_no_tyres_found", "No tyres found")}</p>
          </div>
        )}

        {!tyreSearch.isSearching && !!tyreSearch.tyres.length && <ReactTable data={tyreSearch.tyres} columns={columns} />}
      </Modal.Content>

      <Modal.Actions className="tyre-search-actions">
        <Button className="light" type="button" onClick={handleClose}>
          {t("v8_cancel", "Cancel")}
          <Icon className="xmark" />
        </Button>
        <Button color="green" type="button" onClick={handleConfirm} disabled={!pickedTyre}>
          {t("v8_confirm", "Confirm")}
          <Icon className="check" />
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
